import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { Puzzle, Zap, Shield, RefreshCw, Database, Globe, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/integrations')({
  component: IntegrationsPage,
});

const benefits = [
  {
    iconName: "Zap",
    title: "One-Click Setup",
    description: "Connect to services in seconds with OAuth flows and pre-configured settings.",
  },
  {
    iconName: "Shield",
    title: "Secure by Default",
    description: "All connections use encrypted credentials with fine-grained access control.",
  },
  {
    iconName: "RefreshCw",
    title: "Real-Time Sync",
    description: "Keep data synchronized across systems with automatic webhooks and polling.",
  },
  {
    iconName: "Database",
    title: "Any Data Source",
    description: "REST, GraphQL, gRPC, databases, file systems, and custom protocols.",
  },
];

const categories = [
  {
    name: "CRM & Sales",
    providers: ["Salesforce", "HubSpot", "Pipedrive", "Zoho CRM", "Close", "Copper"],
  },
  {
    name: "Marketing",
    providers: ["Mailchimp", "SendGrid", "Klaviyo", "ActiveCampaign", "Intercom", "Drift"],
  },
  {
    name: "Databases",
    providers: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Snowflake", "BigQuery"],
  },
  {
    name: "Cloud Providers",
    providers: ["AWS", "Google Cloud", "Azure", "DigitalOcean", "Cloudflare", "Vercel"],
  },
  {
    name: "AI & ML",
    providers: ["OpenAI", "Anthropic", "Google Gemini", "Cohere", "Replicate", "Hugging Face"],
  },
  {
    name: "Productivity",
    providers: ["Slack", "Microsoft Teams", "Notion", "Airtable", "Asana", "Jira"],
  },
];

const capabilities = [
  "OAuth 2.0 and API key authentication",
  "Automatic token refresh and credential rotation",
  "Connection health monitoring and alerts",
  "Rate limiting and request throttling",
  "Request/response transformation",
  "Error handling and retries",
  "Audit logging for all API calls",
  "Field-level access control",
];

const faqItems = [
  {
    question: "How do I connect a new integration?",
    answer: "Navigate to Integrations, click 'Add Integration', select your provider, and follow the OAuth flow or enter your API credentials. Most integrations can be set up in under a minute.",
  },
  {
    question: "What if my service isn't in the catalog?",
    answer: "You can create custom integrations using our API builder. Import an OpenAPI spec or manually define endpoints. Any REST API can be integrated in minutes.",
  },
  {
    question: "How are credentials stored?",
    answer: "All credentials are encrypted at rest using AES-256 and stored in our secure secrets management system. We support integration with your own secrets manager if required.",
  },
  {
    question: "Can I set different access levels for integrations?",
    answer: "Yes, you can control which team members can access each integration and define field-level access policies. Some users can read data while others can write, for example.",
  },
];

function IntegrationsPage() {
  return (
    <div>
      <ContentHeader
        title="2000+ Pre-Built Integrations"
        description="Connect to any service instantly. Our growing library of integrations includes CRM, marketing, databases, cloud providers, AI services, and more—all with enterprise-grade security."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "Integrations" },
        ]}
        badge="Feature"
      />

      {/* Benefits */}
      <ContentSection
        id="benefits"
        title="Why Use Pre-Built Integrations?"
        background="muted"
      >
        <FeatureShowcase features={benefits} columns={4} variant="icons" />
      </ContentSection>

      {/* Categories */}
      <ContentSection
        id="categories"
        title="Integration Categories"
        description="Connect to the services your business depends on."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <div key={category.name} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{category.name}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {category.providers.map((provider) => (
                  <span
                    key={provider}
                    className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs"
                  >
                    {provider}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-muted-foreground mt-8">
          And 1900+ more integrations...
        </p>
      </ContentSection>

      {/* Capabilities */}
      <ContentSection
        id="capabilities"
        title="Enterprise Capabilities"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {capabilities.map((capability) => (
            <div key={capability} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{capability}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* How It Works */}
      <ContentSection
        id="how-it-works"
        title="How It Works"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              1
            </div>
            <h3 className="mt-4 font-semibold">Choose Integration</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Browse our catalog and select the service you want to connect.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              2
            </div>
            <h3 className="mt-4 font-semibold">Authenticate</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Complete OAuth flow or enter your API credentials securely.
            </p>
          </div>
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
              3
            </div>
            <h3 className="mt-4 font-semibold">Start Using</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Access the integration in workflows, agents, or directly via API.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Connect Your Services Today"
        description="Start integrating your tools in minutes, not months. Try Matricks free for 14 days."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "View Pricing", href: "/pricing" }}
        background="primary"
      />
    </div>
  );
}
